import { createSha256Hex, encodeUtf8 } from './puppyPackageUtils'
import { canonicalizePuppyCadNumber } from './canonicalPuppyCadNumber'

/**
 * 离线包内容摘要中的单文件记录。
 */
interface PuppyPackageContentDigestEntry {
  /** ZIP 内部业务文件路径。 */
  readonly path: string
  /** 文件原始字节长度。 */
  readonly byteLength: number
  /** 文件字节 SHA-256。 */
  readonly sha256: string
}

/**
 * 判断值是否为可稳定枚举键的普通对象。
 *
 * @param value 待判断值。
 * @returns 是否为普通对象。
 */
function isPlainObject(value: object): value is Record<string, unknown> {
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

/**
 * 创建规范 JSON 错误。
 *
 * @param path 非法值所在 JSON path。
 * @param reason 非法原因。
 * @returns 带精确路径的错误。
 */
function createCanonicalJsonError(path: string, reason: string): Error {
  return new Error(`PuppyCAD 离线包规范 JSON 无法序列化 ${path}: ${reason}`)
}

/**
 * 将未知值转换为键顺序稳定的 JSON 兼容值。
 *
 * @param value 当前值。
 * @param path 当前 JSON path。
 * @param ancestors 当前祖先集合，用于拒绝循环引用。
 * @param isArrayItem 当前值是否位于数组内。
 * @returns 规范化后的 JSON 值；对象属性为 undefined 时返回 undefined 以便省略。
 */
function canonicalizeJsonValue(
  value: unknown,
  path: string,
  ancestors: Set<object>,
  isArrayItem: boolean,
): unknown {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw createCanonicalJsonError(path, '数字必须为有限值')
    }
    return canonicalizePuppyCadNumber(value)
  }
  if (value === undefined) {
    return isArrayItem ? null : undefined
  }
  if (typeof value === 'bigint') {
    throw createCanonicalJsonError(path, '不支持 BigInt')
  }
  if (typeof value === 'function') {
    throw createCanonicalJsonError(path, '不支持函数')
  }
  if (typeof value === 'symbol') {
    throw createCanonicalJsonError(path, '不支持 Symbol')
  }
  if (typeof value !== 'object') {
    throw createCanonicalJsonError(path, `不支持 ${typeof value}`)
  }
  if (ancestors.has(value)) {
    throw createCanonicalJsonError(path, '存在循环引用')
  }

  ancestors.add(value)
  try {
    if (Array.isArray(value)) {
      return value.map((item, index) => canonicalizeJsonValue(
        item,
        `${path}[${index}]`,
        ancestors,
        true,
      ))
    }
    if (!isPlainObject(value)) {
      throw createCanonicalJsonError(path, `仅支持普通对象，收到 ${value.constructor?.name ?? '未知对象'}`)
    }

    const normalized: Record<string, unknown> = {}
    Object.keys(value).sort().forEach((key) => {
      const normalizedValue = canonicalizeJsonValue(
        value[key],
        `${path}.${key}`,
        ancestors,
        false,
      )
      if (normalizedValue !== undefined) {
        normalized[key] = normalizedValue
      }
    })
    return normalized
  } finally {
    ancestors.delete(value)
  }
}

/**
 * 以稳定对象键顺序序列化 PuppyCAD 离线包 JSON。
 *
 * 数组顺序属于装配树、Mate 和拓扑等业务语义，必须保持原序；只有普通对象键按字典序排序。
 * 非 JSON 数据会携带精确路径直接失败，避免静默丢字段后生成不可恢复包。
 *
 * @param value 待序列化数据。
 * @returns 使用两个空格缩进、不追加末尾换行的规范 JSON。
 */
export function stringifyCanonicalPuppyJson(value: unknown): string {
  const normalized = canonicalizeJsonValue(value, '$', new Set<object>(), false)
  if (normalized === undefined) {
    throw createCanonicalJsonError('$', '根值不能为 undefined')
  }
  return JSON.stringify(normalized, null, 2)
}

/**
 * 根据离线包内部业务文件计算稳定内容摘要。
 *
 * 设计说明：先分别计算每个文件的 SHA-256，再摘要规范化文件索引，避免为了一个总摘要
 * 把 GLB、BRep 和 topology-runtime 再拼接成同等大小的大缓冲区。
 *
 * @param files 不包含 manifest.json 的内部业务文件表。
 * @returns 64 位小写十六进制 SHA-256。
 */
export async function createPuppyPackageContentDigest(
  files: Readonly<Record<string, Uint8Array>>,
): Promise<string> {
  const paths = Object.keys(files).sort()
  const entries: PuppyPackageContentDigestEntry[] = []
  for (const path of paths) {
    const normalizedPath = path.trim()
    const bytes = files[path]
    if (!normalizedPath || !bytes) {
      throw new Error(`PuppyCAD 离线包内容摘要包含无效文件路径: ${path}`)
    }
    entries.push({
      path: normalizedPath,
      byteLength: bytes.byteLength,
      sha256: await createSha256Hex(bytes),
    })
  }
  return createSha256Hex(encodeUtf8(stringifyCanonicalPuppyJson(entries)))
}

/**
 * 从稳定内容摘要创建离线包 ID。
 *
 * @param contentDigest 64 位小写 SHA-256。
 * @returns 不依赖墙上时间和随机数的包 ID。
 */
export function createPuppyContentPackageId(contentDigest: string): string {
  const normalizedDigest = contentDigest.trim().toLowerCase()
  if (!/^[a-f0-9]{64}$/.test(normalizedDigest)) {
    throw new Error(`PuppyCAD 离线包内容摘要无效: ${contentDigest}`)
  }
  return `puppy-content-${normalizedDigest}`
}
